import { gql, ApolloClient } from "@apollo/client";
import { VARIANT_SEPARATOR } from "../tokens/variantName";

/**
 * Access At A Glance counts people who came through the access gates into
 * the park. Shuttle tickets (ticketing category "Shuttle") are scanned when a
 * guest boards a bus, not at a park gate, so they are neither ingress nor
 * egress. A shuttle ticket sold with modifiers mints token names like
 * "<item> | <hotel> | <time>" (see variantName.ts), so the base name and its
 * variants are both excluded.
 */
export const SHUTTLE_CATEGORY = "Shuttle";

export const SHUTTLE_TICKET_NAMES = gql`
  query shuttle_ticket_names($where: items_ticketing_bool_exp!) {
    items_ticketing(where: $where) {
      name
      event_id
    }
  }
`;

export async function fetchShuttleTicketNames(
  client: ApolloClient<any>,
  organizationId: number | null | undefined,
  eventIds?: Array<string | number>
): Promise<string[]> {
  const where: any = {
    category: { _eq: SHUTTLE_CATEGORY },
    is_active: { _eq: true },
    event: { organization_id: { _eq: organizationId } },
  };
  if (eventIds?.length) where.event_id = { _in: eventIds };

  const { data } = await client.query({
    query: SHUTTLE_TICKET_NAMES,
    variables: { where },
    fetchPolicy: "network-only",
  });

  const names: string[] = (data?.items_ticketing || [])
    .map((item: { name?: string }) => (item.name || "").trim())
    .filter(Boolean);
  return Array.from(new Set(names));
}

// Postgres LIKE: a backslash escapes the wildcards.
const escapeLike = (value: string) => value.replace(/[\\%_]/g, (c) => `\\${c}`);

const SUCCESSFUL = { scan_status: { _ilike: "success" } };

/** reports_access conditions for a successful scan through a gate into the park. */
export function ingressConditions(shuttleTicketNames: string[]): any[] {
  const notShuttleTicket = shuttleTicketNames.flatMap((name) => {
    const base = escapeLike(name);
    return [
      { access_token_type: { _nilike: base } },
      { access_token_type: { _nilike: `${base}${VARIANT_SEPARATOR}%` } },
    ];
  });
  return [
    SUCCESSFUL,
    { access_token_type: { _nilike: "%egress%" } },
    { access_token_type: { _nilike: "%shuttle%" } },
    ...notShuttleTicket,
  ];
}

/** reports_access conditions for a successful scan out through an egress gate. */
export function egressConditions(): any[] {
  return [SUCCESSFUL, { access_token_type: { _ilike: "%egress%" } }];
}
